import './EditionRegistrationForm.scss'

import {
	FormDataType,
	FormSectionType,
	FormStepType
} from '../../../blocks/FormBlock'
import React, {useState} from 'react'

import {Blocks} from '../../../blocks/Blocks'
import {Language} from '../../../../copy'
import {Theme} from '../../../Theme'
import authorisationField from './fields/authorisationField'
import emailField from './fields/emailField'
import firstNameField from './fields/firstNameField'
import individualInfoSection from './sections/individualInfoSection'
import lastNameField from './fields/lastNameField'
import personalInfoSection from './sections/personalInfoSection'
import {style} from '../../../../util/style'
import teamInfoSection from './sections/teamInfoSection'
import typeSection from './sections/typeSection'
import {useHistory} from 'react-router'
import {useTranslation} from '../../../../util/i18n'

const useTypeStep = (step: number, stepLabel: number): FormStepType => {
	const t = useTranslation().events.register
	return {
		id: step,
		parent_id: 0,
		channel: 'step',
		label: `${t.step} ${stepLabel}`,
		sections: [
			{...typeSection, parent_id: step, label: `${t.step} ${stepLabel}`}
		],
		submit_text: t.next_step
	}
}

const getIndividualStep = (
	step: number,
	stepLabel: number,
	t: Language['events']['register']
): FormStepType => {
	return {
		id: step,
		parent_id: 0,
		channel: 'step',
		label: `${t.step} ${stepLabel}`,
		sections: [
			{
				...individualInfoSection,
				parent_id: step,
				label: `${t.step} ${stepLabel}`
			}
		],
		submit_text: t.next_step
	}
}

const getTeamStep = (
	step: number,
	stepLabel: number,
	t: Language['events']['register']
): FormStepType => {
	return {
		id: step,
		parent_id: 0,
		channel: 'step',
		label: `${t.step} ${stepLabel}`,
		sections: [
			{...teamInfoSection, parent_id: step, label: `${t.step} ${stepLabel}`}
		],
		submit_text: t.next_step
	}
}

export const EditionRegistrationForm: React.FC<{
	registration_type_individual: boolean
	registration_type_team: boolean
	registration_teamSections: Array<FormSectionType> | null
	reCaptchaKey: string
}> = ({
	registration_type_individual,
	registration_type_team,
	registration_teamSections,
	reCaptchaKey
}) => {
	const t = useTranslation().events.register
	const maxTeamMembers: number = 15
	if (!registration_type_individual && !registration_type_team) return null

	const [data, setData] = useState<{[key: string]: any}>({})

	const [numberOfTeamMembers, setNumberOfTeamMembers] = useState<number>(
		registration_type_individual && !registration_type_team ? 1 : null
	)

	const styles = style('editionregistrationform')

	const canChooseType: boolean =
		registration_type_individual && registration_type_team

	const [isIndividual, setIsIndividual] = useState<boolean>(
		registration_type_individual && !registration_type_team
	)

	const [isTeam, setIsTeam] = useState<boolean>(
		registration_type_team && !registration_type_individual
	)

	const getData = (): {[key: string]: any} => {
		if (canChooseType) return {...data, '2-1-2': numberOfTeamMembers}
		return {
			...data,
			'1-1-1': isIndividual ? 'individual' : 'team',
			'2-1-2': numberOfTeamMembers
		}
	}

	const getPersonalStep = (step: number, stepLabel: number): FormStepType => {
		const section = {
			...personalInfoSection,
			parent_id: step,
			label: `${t.step} ${stepLabel}`,
			fields: [...personalInfoSection.fields]
		}
		let numberOfFields = section.fields.length

		if (isTeam) {
			// add the subtitle before the fields
			section.fields.unshift(
				<div className={styles('teammember')}>{teamOwnerTitle}</div>
			)
			// add the team members
			for (let i: number = 2; i <= numberOfTeamMembers; i++) {
				section.fields.push(
					<div className={styles('teammember')}>
						{[
							teamMemberTitle(i),
							numberOfTeamMembers > 2 && i === numberOfTeamMembers
								? removeTeamMemberButton
								: null
						]}
					</div>,
					{...firstNameField, id: numberOfFields + 1, parent_id: section.id},
					{...lastNameField, id: numberOfFields + 2, parent_id: section.id},
					{...emailField, id: numberOfFields + 3, parent_id: section.id}
				)
				numberOfFields += 3
			}
			// add the add another team member button
			if (numberOfTeamMembers < maxTeamMembers) {
				section.fields.push(addTeamMemberButton)
			}
			section.fields.push({
				...authorisationField,
				id: numberOfFields + 1,
				parent_id: section.id
			})
		}

		return {
			id: step,
			parent_id: 0,
			channel: 'step',
			label: `${t.step} ${stepLabel}`,
			sections: [section],
			submit_text: registration_teamSections
				? t.next_step
				: isIndividual
				? t.subscribe_individual
				: t.subscribe_team
		}
	}

	const getTeamSectionsStep = (
		step: number,
		stepLabel: number
	): FormStepType => {
		return {
			id: step,
			parent_id: 0,
			channel: 'step',
			label: `${t.step} ${stepLabel}`,
			sections: registration_teamSections,
			submit_text: isIndividual ? t.subscribe_individual : t.subscribe_team
		}
	}

	const correctNumberOfTeamMembers = (value: number): void => {
		if (isIndividual) {
			setNumberOfTeamMembers(1)
		} else if (isTeam && value < 2) {
			setNumberOfTeamMembers(2)
		} else if (isTeam && value > maxTeamMembers) {
			setNumberOfTeamMembers(maxTeamMembers)
		} else {
			setNumberOfTeamMembers(value)
		}
	}

	const onChangeHandler = (
		key: string,
		value: any,
		currentFormData: FormDataType
	) => {
		if (canChooseType)
			switch (key) {
				case '1-1-1':
					if (value === 'individual') {
						setIsIndividual(true)
						setIsTeam(false)
						setNumberOfTeamMembers(1)
					} else if (value === 'team') {
						setIsTeam(true)
						setIsIndividual(false)
						if (numberOfTeamMembers === 1) setNumberOfTeamMembers(null)
					}
					break
			}
		switch (key) {
			case '2-1-2':
				correctNumberOfTeamMembers(parseInt(value, 10))
				break
		}
		setData(currentFormData)
	}

	const onSubmitHandler = (response: any) => {
		if (response.uuid && response.thanksPage) {
			const history = useHistory()
			history.push(`${response.thanksPage}?thanks=${response.uuid}`)
		} else {
			console.error('No uuid or thanksPage defined', response)
		}
	}

	const getSteps = (): Array<FormStepType> => {
		let stepLabelNumber: number = 1
		const steps: Array<FormStepType> = []
		if (canChooseType) {
			// user can choose between individual or team
			steps.push(useTypeStep(1, stepLabelNumber++))
		}
		// add individual or team page step
		steps.push(
			isIndividual
				? getIndividualStep(2, stepLabelNumber++, t)
				: getTeamStep(2, stepLabelNumber++, t)
		)
		// add personal information step
		steps.push(getPersonalStep(3, stepLabelNumber++))
		if (registration_teamSections) {
			// add extra team questions
			steps.push(getTeamSectionsStep(4, stepLabelNumber++))
		}
		return steps
	}

	const removeTeamMemberClickHandler = () => {
		const numberOfTeamCreatorFields = personalInfoSection.fields.length
		const numberOfTeamMemberFields = 3
		const newNumberOfTeamMembers: number = numberOfTeamMembers - 1
		const id =
			1 +
			numberOfTeamCreatorFields +
			newNumberOfTeamMembers * numberOfTeamMemberFields
		const newData = {}
		for (let i: number = 0; i < numberOfTeamMemberFields; i++) {
			newData[`3-1-${id - i}`] = null
		}
		newData[`3-1-${id - numberOfTeamMemberFields}`] = data[`3-1-${id}`]
		setData({
			...data,
			...newData
		})
		setNumberOfTeamMembers(newNumberOfTeamMembers)
	}

	const addTeamMemberClickHandler = () => {
		const numberOfTeamCreatorFields = personalInfoSection.fields.length
		const numberOfTeamMemberFields = 3

		const authorisationFieldId: number =
			1 +
			numberOfTeamCreatorFields +
			(numberOfTeamMembers - 1) * numberOfTeamMemberFields

		if (typeof data[`3-1-${authorisationFieldId}`] !== 'undefined') {
			setData({
				...data,
				[`3-1-${authorisationFieldId}`]: '',
				[`3-1-${authorisationFieldId + numberOfTeamMemberFields}`]: data[
					`3-1-${authorisationFieldId}`
				]
			})
		} else {
			setData({
				...data,
				[`3-1-${authorisationFieldId}`]: ''
			})
		}
		setNumberOfTeamMembers(numberOfTeamMembers + 1)
	}

	const teamOwnerTitle = (
		<Theme.H4 className={styles('teammember-title')} mod="green" key={0}>
			{t.teamowner}
		</Theme.H4>
	)

	const teamMemberTitle = (index: number) => (
		<Theme.H4 className={styles('teammember-title')} mod="green">
			{t.teammember} {index}
		</Theme.H4>
	)

	const removeTeamMemberButton = (
		<div className={styles('teammember-remove')}>
			<a
				className={styles('teammember-remove-link')}
				onClick={removeTeamMemberClickHandler}
			>
				<span className={styles('teammember-remove-link-icon')} />
				<span className={styles('teammember-remove-link-text')}>
					{t.remove_teammember}
				</span>
			</a>
		</div>
	)

	const addTeamMemberButton = (
		<div className={styles('teammember-add')}>
			<a
				className={styles('teammember-add-link')}
				onClick={addTeamMemberClickHandler}
			>
				<span className={styles('teammember-add-link-icon')} />
				<span className={styles('teammember-add-link-text')}>
					{t.add_teammember}
				</span>
			</a>
		</div>
	)

	return (
		<div className={styles()}>
			<Theme.Container shifted="right">
				<Blocks
					blocks={[
						{
							type: 'form' as 'form',
							path: '',
							reCaptchaKey,
							requestUri: window.location.pathname,
							id: 1,
							pageId: 0,
							steps: getSteps(),
							data: getData(),
							useEffect: true,
							onChange: onChangeHandler,
							onSubmit: onSubmitHandler
						}
					]}
				/>
			</Theme.Container>
		</div>
	)
}
